import { PublicKey, SystemProgram } from '@solana/web3.js';

/** Shank discriminant for `Transfer` in pact_c1_sol IDL. */
export const C1_SOL_TRANSFER_DISCRIMINANT = 0;

/** From `pact_c1_sol.onchain.json` metadata.address */
export const C1_SOL_PROGRAM_ID = new PublicKey('9ZNzEdRCHkJkvVnoc3XtqwSafZCdjL7tzogp1Xvj1P5F');

const encodeU32Le = (n: number): Buffer => {
  const buf = Buffer.alloc(4);
  buf.writeUInt32LE(n, 0);
  return buf;
};

const encodeU64Le = (n: bigint): Buffer => {
  const buf = Buffer.alloc(8);
  buf.writeBigUInt64LE(n, 0);
  return buf;
};

const encodeVecU8 = (bytes: Buffer): Buffer => Buffer.concat([encodeU32Le(bytes.length), bytes]);

const assertU64 = (n: bigint, field: string) => {
  if (n < 0n || n > 0xffff_ffff_ffff_ffffn) {
    throw new Error(`${field} is out of u64 range`);
  }
};

export type EncodeC1SolTransferArgsInput = {
  /** 32-byte id: hex (`0x…` / bare), PublicKey, or raw bytes */
  l2LinkedId: PublicKey | string | Uint8Array;
  maxPayment: bigint;
  maxNonce: bigint;
  amount: bigint;
  data?: Buffer | Uint8Array;
};

const stripHexPrefix = (value: string): string =>
  value.startsWith('0x') || value.startsWith('0X') ? value.slice(2) : value;

/** Parse a 32-byte L2 linked id from hex (preferred) or raw bytes. */
export const parseL2LinkedIdBytes = (value: PublicKey | string | Uint8Array): Buffer => {
  if (value instanceof Uint8Array || Buffer.isBuffer(value)) {
    if (value.length !== 32) {
      throw new Error('l2LinkedId must be exactly 32 bytes');
    }
    return Buffer.from(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const hex = stripHexPrefix(trimmed);
    if (/^[0-9a-fA-F]+$/.test(hex)) {
      if (hex.length !== 64) {
        throw new Error('l2LinkedId hex must be exactly 32 bytes (64 hex chars)');
      }
      return Buffer.from(hex, 'hex');
    }
    throw new Error('l2LinkedId must be 32-byte hex (64 hex chars, optional 0x)');
  }

  return Buffer.from(value.toBytes());
};

export const isValidL2LinkedIdHex = (value: string): boolean => {
  try {
    const hex = stripHexPrefix(value.trim());
    return hex.length === 64 && /^[0-9a-fA-F]+$/.test(hex);
  } catch {
    return false;
  }
};

/**
 * Encode `pact_c1_sol::Transfer` instruction data from IDL:
 * `u8 disc=0` + `[u8;32] l2LinkedId` + `u64 maxPayment` + `u64 maxNonce` + `u64 amount` + `bytes data`
 */
export const encodeC1SolTransferArgs = ({
  l2LinkedId,
  maxPayment,
  maxNonce,
  amount,
  data = Buffer.alloc(0),
}: EncodeC1SolTransferArgsInput): Buffer => {
  assertU64(maxPayment, 'maxPayment');
  assertU64(maxNonce, 'maxNonce');
  assertU64(amount, 'amount');

  return Buffer.concat([
    Buffer.from([C1_SOL_TRANSFER_DISCRIMINANT]),
    parseL2LinkedIdBytes(l2LinkedId),
    encodeU64Le(maxPayment),
    encodeU64Le(maxNonce),
    encodeU64Le(amount),
    encodeVecU8(Buffer.from(data)),
  ]);
};

/** PDA seeds from IDL docs: `nonce + l2_linked_id` */
export const findNonceAccountPda = (
  l2LinkedId: PublicKey | string | Uint8Array,
  programId: PublicKey = C1_SOL_PROGRAM_ID
): [PublicKey, number] => {
  return PublicKey.findProgramAddressSync([Buffer.from('nonce'), parseL2LinkedIdBytes(l2LinkedId)], programId);
};

/** PDA seeds from IDL docs: `paid + recipient + l2_linked_id` */
export const findPaidAccountPda = (
  recipient: PublicKey | string,
  l2LinkedId: PublicKey | string | Uint8Array,
  programId: PublicKey = C1_SOL_PROGRAM_ID
): [PublicKey, number] => {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('paid'), new PublicKey(recipient).toBytes(), parseL2LinkedIdBytes(l2LinkedId)],
    programId
  );
};

export type BuildC1SolTransferAccountsInput = {
  payer: PublicKey | string;
  recipient: PublicKey | string;
  l2LinkedId: PublicKey | string | Uint8Array;
  programId?: PublicKey | string;
};

export const buildC1SolTransferAccounts = ({
  payer,
  recipient,
  l2LinkedId,
  programId = C1_SOL_PROGRAM_ID,
}: BuildC1SolTransferAccountsInput) => {
  const program = new PublicKey(programId);
  const recipientKey = new PublicKey(recipient);
  const [nonceAccount] = findNonceAccountPda(l2LinkedId, program);
  const [paidAccount] = findPaidAccountPda(recipientKey, l2LinkedId, program);

  return {
    nonceAccount,
    paidAccount,
    keys: [
      { pubkey: new PublicKey(payer), isSigner: true, isWritable: true },
      { pubkey: recipientKey, isSigner: false, isWritable: true },
      { pubkey: nonceAccount, isSigner: false, isWritable: true },
      { pubkey: paidAccount, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
  };
};

export const isValidSolanaPublicKey = (value: string): boolean => {
  try {
    new PublicKey(value);
    return true;
  } catch {
    return false;
  }
};

export const parseOptionalHexData = (raw: string): Buffer => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return Buffer.alloc(0);
  }

  const hex = trimmed.startsWith('0x') || trimmed.startsWith('0X') ? trimmed.slice(2) : trimmed;
  if (hex.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hex)) {
    throw new Error('Extra data must be a valid hex string');
  }

  return Buffer.from(hex, 'hex');
};

/** NonceAccount / PaidAccount each store one u64. */
export const C1_SOL_STATE_ACCOUNT_SPACE = 8;

export type TransferFundingCheckInput = {
  connection: {
    getAccountInfo: (publicKey: PublicKey) => Promise<{ lamports: number; data: { length: number } } | null>;
    getBalance: (publicKey: PublicKey) => Promise<number>;
    getMinimumBalanceForRentExemption: (dataLength: number) => Promise<number>;
  };
  payer: PublicKey | string;
  recipient: PublicKey | string;
  nonceAccount: PublicKey;
  paidAccount: PublicKey;
  amount: bigint;
};

export type TransferFundingCheckResult = {
  ok: boolean;
  recipientRentExempt: number;
  pdaRentExempt: number;
  estimatedPayerNeed: bigint;
  messages: string[];
};

/**
 * Catch common InsufficientFundsForRent cases before simulate/send:
 * - funding a new/empty recipient with amount below rent-exempt
 * - creating nonce/paid PDAs without enough payer balance
 */
export const checkC1SolTransferFunding = async ({
  connection,
  payer,
  recipient,
  nonceAccount,
  paidAccount,
  amount,
}: TransferFundingCheckInput): Promise<TransferFundingCheckResult> => {
  const payerKey = new PublicKey(payer);
  const recipientKey = new PublicKey(recipient);

  const [recipientInfo, nonceInfo, paidInfo, payerBalance, recipientRentExempt, pdaRentExempt] = await Promise.all([
    connection.getAccountInfo(recipientKey),
    connection.getAccountInfo(nonceAccount),
    connection.getAccountInfo(paidAccount),
    connection.getBalance(payerKey),
    connection.getMinimumBalanceForRentExemption(0),
    connection.getMinimumBalanceForRentExemption(C1_SOL_STATE_ACCOUNT_SPACE),
  ]);

  const messages: string[] = [];
  let estimatedPayerNeed = amount;

  const isRecipientNew = !recipientInfo || recipientInfo.lamports === 0;
  if (isRecipientNew && amount > 0n && amount < BigInt(recipientRentExempt)) {
    messages.push(
      `Recipient is empty/new: amount must be at least ${recipientRentExempt} lamports (rent-exempt), got ${amount.toString()}.`
    );
  }

  if (!nonceInfo) {
    estimatedPayerNeed += BigInt(pdaRentExempt);
  }
  if (!paidInfo) {
    estimatedPayerNeed += BigInt(pdaRentExempt);
  }

  // Leave headroom for the signature fee.
  const feeHeadroom = 10_000n;
  estimatedPayerNeed += feeHeadroom;

  if (BigInt(payerBalance) < estimatedPayerNeed) {
    messages.push(
      `Payer balance ${payerBalance} lamports is below estimated need ${estimatedPayerNeed.toString()} ` +
        `(amount + PDA rents${nonceInfo ? '' : ' + nonce'}${!paidInfo ? ' + paid' : ''} + fees).`
    );
  }

  return {
    ok: messages.length === 0,
    recipientRentExempt,
    pdaRentExempt,
    estimatedPayerNeed,
    messages,
  };
};

export const formatSimulationError = (err: unknown): string => {
  const raw = JSON.stringify(err);
  if (raw.includes('InsufficientFundsForRent')) {
    return (
      `${raw}\n\nUsually account_index 1 is the recipient: sending to a new/empty account requires ` +
      `amount ≥ rent-exempt minimum (~890880 lamports). PDAs (nonce/paid) also need rent from the payer.`
    );
  }
  return raw;
};
